import * as XLSX from "xlsx";
import { ShortfyEvent } from "../../types/event.types";
import {
  EventRegistration,
  MealPreference,
  RegistrationStatus,
} from "../../types/shared.types";

type RegistrationStats = {
  totalRegistrations: number;
  confirmedCount: number;
  tentativeCount: number;
  declinedCount: number;
  cancelledCount: number;
  attendedCount: number;
  mealStats: {
    none: number;
    meat: number;
    vegetarian: number;
    vegan: number;
    flex: number;
    noMeal: number;
  };
  capacityRemaining: number;
  totalParticipants: number;
  totalAdults: number;
  totalChildren: number;
};

const formatDateTime = (isoString: string) => {
  const d = new Date(isoString);
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (isoString: string) => {
  const d = new Date(isoString);
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const getDurationText = (startIso: string, endIso: string) => {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const durationMs = end.getTime() - start.getTime();
  const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
  const durationMinutes = Math.floor(
    (durationMs % (1000 * 60 * 60)) / (1000 * 60),
  );
  const durationDays = Math.floor(durationHours / 24);

  if (durationDays > 0) {
    let txt = `${durationDays} day${durationDays > 1 ? "s" : ""}`;
    const remHours = durationHours % 24;
    if (remHours > 0) txt += `, ${remHours} hour${remHours > 1 ? "s" : ""}`;
    return txt;
  }
  if (durationHours > 0) {
    let txt = `${durationHours} hour${durationHours > 1 ? "s" : ""}`;
    if (durationMinutes > 0)
      txt += `, ${durationMinutes} minute${durationMinutes > 1 ? "s" : ""}`;
    return txt;
  }
  return `${durationMinutes} minute${durationMinutes > 1 ? "s" : ""}`;
};

// NEW: maps backend RegistrationStatus to display labels for the export
const participationStatusLabel = (status: RegistrationStatus): string => {
  switch (status) {
    case RegistrationStatus.Registered: return "Registered";
    case RegistrationStatus.Attended:   return "Checked-in";
    case RegistrationStatus.Cancelled:  return "Cancelled";
    case RegistrationStatus.Declined:   return "Declined";
    case RegistrationStatus.Tentative:  return "Tentative";
    default:                            return status;
  }
};

const mealLabelShort = (pref: MealPreference) => {
  switch (pref) {
    case MealPreference.Meat:
      return "Meat";
    case MealPreference.Vegetarian:
      return "Veg";
    case MealPreference.Vegan:
      return "Vegan";
    case MealPreference.Flex:
      return "Flex";
    case MealPreference.NoMeal:
      return "No Meal Required";
    default:
      return "-";
  }
};

// ── Cell style helpers ───────────────────────────────────────────────────────

const STYLE_SECTION_HEADER = {
  font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "1F4E79" } },
  alignment: { horizontal: "left" as const },
};

const STYLE_COL_HEADER = {
  font: { bold: true, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "2E75B6" } },
  alignment: { horizontal: "center" as const },
};

const STYLE_LABEL = {
  font: { bold: true },
  fill: { fgColor: { rgb: "D9E1F2" } },
};

const STYLE_VALUE = {
  alignment: { wrapText: true },
};

const STYLE_STAT_VALUE = {
  font: { bold: true, sz: 12 },
  alignment: { horizontal: "center" as const },
};

const setCellStyled = (
  ws: XLSX.WorkSheet,
  row: number,
  col: number,
  value: string | number,
  style: object,
) => {
  const ref = XLSX.utils.encode_cell({ r: row, c: col });
  ws[ref] = { v: value, t: typeof value === "number" ? "n" : "s", s: style };
};

const setCell = (
  ws: XLSX.WorkSheet,
  row: number,
  col: number,
  value: string | number,
) => {
  const ref = XLSX.utils.encode_cell({ r: row, c: col });
  ws[ref] = { v: value, t: typeof value === "number" ? "n" : "s" };
};

// ── Main export ──────────────────────────────────────────────────────────────

export const exportEventToXLSX = async (event: ShortfyEvent): Promise<void> => {
  const backendUrlRaw = process.env.REACT_APP_BACKEND_URL || "";
  const backendUrl = backendUrlRaw.startsWith("http")
    ? backendUrlRaw.replace(/\/$/, "")
    : window.location.origin;

  // OLD: used authToken from localStorage with Authorization header — backend ignores this
  // const token = localStorage.getItem("authToken") || "";
  // headers["Authorization"] = `Bearer ${token}`;

  // NEW: backend requireAuth middleware reads client-principal-user-id from sessionStorage
  const userId = sessionStorage.getItem("userOid") || "";
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (userId) headers["client-principal-user-id"] = userId;

  // Fetch registrations
  let registrations: EventRegistration[] = [];
  try {
    // OLD: `${backendUrl}/api/registrations/${event.eventId}` — excluded Declined participants
    // NEW: includeDeclined=true ensures ALL participants regardless of status are returned
    const res = await fetch(
      `${backendUrl}/api/registrations/${event.eventId}?includeDeclined=true`,
      { credentials: "include", headers },
    );
    if (res.ok) {
      const json = await res.json();
      registrations = json.data || [];
    } else {
      console.error("[XLSX Export] Registrations fetch failed:", res.status, await res.text());
    }
  } catch (err) {
    console.error("[XLSX Export] Registrations fetch error:", err);
  }

  // Fetch stats
  let stats: RegistrationStats = {
    totalRegistrations: event.currentRegistrations || 0,
    confirmedCount: event.currentRegistrations || 0,
    tentativeCount: 0,
    declinedCount: 0,
    cancelledCount: 0,
    attendedCount: 0,
    mealStats: { none: 0, meat: 0, vegetarian: 0, vegan: 0, flex: 0, noMeal: 0 },
    capacityRemaining: event.maxCapacity - (event.currentRegistrations || 0),
    totalParticipants: 0,
    totalAdults: 0,
    totalChildren: 0,
  };
  try {
    const res = await fetch(
      `${backendUrl}/api/registrations/${event.eventId}/stats`,
      { credentials: "include", headers },
    );
    if (res.ok) {
      const json = await res.json();
      if (json.data) stats = json.data;
    }
  } catch {}

  const hasEventEnded = new Date(event.eventEndDate) < new Date();

  // OLD: only confirmed attendees were exported
  // const confirmed = registrations.filter(
  //   (r) =>
  //     r.status === RegistrationStatus.Registered ||
  //     r.status === RegistrationStatus.Attended,
  // );

  // NEW: export all participants regardless of status
  const participants = registrations;

  const ws: XLSX.WorkSheet = {};
  let row = 0;

  // ── Event Details ──────────────────────────────────────────────────────
  setCellStyled(ws, row, 0, "Event Details", STYLE_SECTION_HEADER);
  setCellStyled(ws, row, 1, "", STYLE_SECTION_HEADER);
  row++;

  setCellStyled(ws, row, 0, "Field", STYLE_COL_HEADER);
  setCellStyled(ws, row, 1, "Value", STYLE_COL_HEADER);
  row++;

  const eventDetails: [string, string][] = [
    ["Name", event.eventName],
    ["Start", formatDateTime(event.eventStartDate)],
    ["End", formatDateTime(event.eventEndDate)],
    ["Location", event.location],
    ["Duration", getDurationText(event.eventStartDate, event.eventEndDate)],
    ["Deadline", formatDate(event.registrationDeadline)],
    ["Status", event.status],
    ["Capacity", `${event.currentRegistrations} / ${event.maxCapacity}`],
  ];

  for (const [label, value] of eventDetails) {
    setCellStyled(ws, row, 0, label, STYLE_LABEL);
    setCellStyled(ws, row, 1, value, STYLE_VALUE);
    row++;
  }

  row++; // blank row

  // ── Registration Statistics ────────────────────────────────────────────
  const statHeaders = hasEventEnded
    ? ["Total", "Confirmed", "Tentative", "Attended", "Declined", "Cancelled"]
    : ["Total", "Confirmed", "Tentative", "Declined", "Cancelled"];

  const statValues = hasEventEnded
    ? [
        stats.totalRegistrations,
        stats.confirmedCount,
        stats.tentativeCount,
        stats.attendedCount,
        stats.declinedCount,
        stats.cancelledCount,
      ]
    : [
        stats.totalRegistrations,
        stats.confirmedCount,
        stats.tentativeCount,
        stats.declinedCount,
        stats.cancelledCount,
      ];

  setCellStyled(ws, row, 0, "Registration Statistics", STYLE_SECTION_HEADER);
  for (let i = 1; i < statHeaders.length; i++) {
    setCellStyled(ws, row, i, "", STYLE_SECTION_HEADER);
  }
  row++;

  statHeaders.forEach((h, i) => setCellStyled(ws, row, i, h, STYLE_COL_HEADER));
  row++;

  statValues.forEach((v, i) => setCellStyled(ws, row, i, v, STYLE_STAT_VALUE));
  row++;

  row++; // blank row

  // ── Participant Breakdown ──────────────────────────────────────────────
  if (event.additionalGuestsEnabled) {
    const participantBreakdownHeaders = ["Total Participants", "Partners", "Additional Adults", "Additional Children"];
    const participantBreakdownValues = [
      stats.totalParticipants,
      stats.confirmedCount + stats.attendedCount,
      stats.totalAdults,
      stats.totalChildren,
    ];

    setCellStyled(ws, row, 0, "Participant Breakdown", STYLE_SECTION_HEADER);
    for (let i = 1; i < participantBreakdownHeaders.length; i++) {
      setCellStyled(ws, row, i, "", STYLE_SECTION_HEADER);
    }
    row++;

    participantBreakdownHeaders.forEach((h, i) => setCellStyled(ws, row, i, h, STYLE_COL_HEADER));
    row++;

    participantBreakdownValues.forEach((v, i) => setCellStyled(ws, row, i, v, STYLE_STAT_VALUE));
    row++;

    row++; // blank row
  }

  // ── Meal Preferences ───────────────────────────────────────────────────
  if (event.mealOptionsEnabled) {
    const mealHeaders = ["Meat", "Vegetarian", "Vegan", "Flex", "No Meal Required", "No Pref."];
    const mealValues = [
      stats.mealStats.meat,
      stats.mealStats.vegetarian,
      stats.mealStats.vegan,
      stats.mealStats.flex,
      stats.mealStats.noMeal,
      stats.mealStats.none,
    ];

    setCellStyled(ws, row, 0, "Meal Preferences", STYLE_SECTION_HEADER);
    for (let i = 1; i < mealHeaders.length; i++) {
      setCellStyled(ws, row, i, "", STYLE_SECTION_HEADER);
    }
    row++;

    mealHeaders.forEach((h, i) => setCellStyled(ws, row, i, h, STYLE_COL_HEADER));
    row++;

    mealValues.forEach((v, i) => setCellStyled(ws, row, i, v, STYLE_STAT_VALUE));
    row++;

    row++; // blank row
  }

  // OLD: only confirmed attendees with limited columns
  // const attendeeHeaders = [
  //   "Name", "Emp. ID", "Sector",
  //   ...(event.mealOptionsEnabled ? ["Meal"] : []),
  //   "Notes",
  //   ...(hasEventEnded ? ["Status"] : []),
  // ];
  // setCellStyled(ws, row, 0, `Confirmed Attendees (${confirmed.length})`, STYLE_SECTION_HEADER);
  // for (let i = 1; i < attendeeHeaders.length; i++) setCellStyled(ws, row, i, "", STYLE_SECTION_HEADER);
  // row++;
  // attendeeHeaders.forEach((h, i) => setCellStyled(ws, row, i, h, STYLE_COL_HEADER));
  // row++;
  // confirmed.forEach((r) => {
  //   const rowData: (string | number)[] = [
  //     r.userName, r.employeeId || "-", r.sector || "-",
  //     ...(event.mealOptionsEnabled ? [mealLabelShort(r.mealPreference)] : []),
  //     (r.notes ?? "").trim() || "-",
  //     ...(hasEventEnded
  //       ? [r.status === RegistrationStatus.Attended ? "Attended" : "Confirmed"]
  //       : []),
  //   ];
  //   rowData.forEach((val, i) => setCell(ws, row, i, val));
  //   row++;
  // });

  // NEW: all participants with Name, BU, Sector, Participation Status, Meal (if enabled), Adults/Children (if enabled), Notes
  const participantHeaders = [
    "Name",
    "BU",
    "Sector",
    "Participation Status",
    ...(event.mealOptionsEnabled ? ["Meal Preference"] : []),
    ...(event.additionalGuestsEnabled ? ["Addl. Adults", "Addl. Children"] : []),
    "Notes",
  ];

  setCellStyled(ws, row, 0, `Participants (${participants.length})`, STYLE_SECTION_HEADER);
  for (let i = 1; i < participantHeaders.length; i++) {
    setCellStyled(ws, row, i, "", STYLE_SECTION_HEADER);
  }
  row++;

  participantHeaders.forEach((h, i) => setCellStyled(ws, row, i, h, STYLE_COL_HEADER));
  row++;

  participants.forEach((r) => {
    const rowData: (string | number)[] = [
      r.userName,
      r.cluster || "-",
      r.sector || "-",
      participationStatusLabel(r.status),
      ...(event.mealOptionsEnabled ? [mealLabelShort(r.mealPreference)] : []),
      ...(event.additionalGuestsEnabled ? [r.additionalAdults ?? 0, r.additionalChildren ?? 0] : []),
      (r.notes ?? "").trim() || "-",
    ];
    rowData.forEach((val, i) => setCell(ws, row, i, val));
    row++;
  });

  // ── Finalize worksheet ─────────────────────────────────────────────────
  ws["!ref"] = XLSX.utils.encode_range(
    { r: 0, c: 0 },
    { r: row - 1, c: participantHeaders.length - 1 },
  );
  ws["!cols"] = [
    { wch: 24 }, // Name / Field
    { wch: 20 }, // BU
    { wch: 20 }, // Sector
    { wch: 20 }, // Participation Status
    { wch: 14 }, // Meal (if enabled)
    { wch: 14 }, // Addl. Adults (if enabled)
    { wch: 16 }, // Addl. Children (if enabled)
    { wch: 32 }, // Notes
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Event Report");

  const fileName = `CGI_Event_${event.eventName.replace(/[^a-z0-9]/gi, "_")}_${
    new Date().toISOString().split("T")[0]
  }.xlsx`;

  XLSX.writeFile(wb, fileName);
};